/**
 * plugins/code-example.ts — Blocos de código (pre.code-example)
 *
 * Ferramentas: editar código raw, trocar linguagem, toggle line-wrap,
 *              inserir token de highlight (.kw .str .cm .fn .nu .sp),
 *              limpar highlights, duplicar bloco.
 *
 * Tokens CSS de syntax highlight:
 *   .kw  — keywords     (public, function, if, return, const, let, import, class...)
 *   .str — strings       ("...", '...', template literals)
 *   .cm  — comments      (// ..., /* ... *✕, #, --, <!-- -->)
 *   .fn  — funções/tipos (nomes de funções, classes, tipos)
 *   .nu  — números       (42, 3.14, 0xFF)
 *   .sp  — especiais     (variáveis, $var, @decorators, this)
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside } from '../types';
import { tokenize } from './code-tokenizer';

/** Linguagens disponíveis para o select */
const LANGUAGES: { value: string; label: string }[] = [
    { value: '',           label: '— Nenhuma —' },
    { value: 'php',        label: 'PHP' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python',     label: 'Python' },
    { value: 'java',       label: 'Java' },
    { value: 'csharp',     label: 'C#' },
    { value: 'go',         label: 'Go' },
    { value: 'rust',       label: 'Rust' },
    { value: 'ruby',       label: 'Ruby' },
    { value: 'sql',        label: 'SQL' },
    { value: 'bash',       label: 'Bash / Shell' },
    { value: 'powershell', label: 'PowerShell' },
    { value: 'html',       label: 'HTML' },
    { value: 'css',        label: 'CSS' },
    { value: 'scss',       label: 'SCSS / Sass' },
    { value: 'json',       label: 'JSON' },
    { value: 'xml',        label: 'XML' },
    { value: 'yaml',       label: 'YAML' },
    { value: 'toml',       label: 'TOML' },
    { value: 'ini',        label: 'INI / .env' },
    { value: 'docker',     label: 'Dockerfile' },
    { value: 'nginx',      label: 'Nginx Config' },
    { value: 'apache',     label: 'Apache Config' },
    { value: 'markdown',   label: 'Markdown' },
    { value: 'diff',       label: 'Diff / Patch' },
    { value: 'plaintext',  label: 'Texto Puro' },
];

/** Mapeamento token → nome amigável */
const TOKEN_MAP: { cls: string; label: string; title: string }[] = [
    { cls: 'kw',  label: 'KW',  title: 'Keyword (public, function, if, return)' },
    { cls: 'str', label: 'STR', title: 'String ("...", \'...\')' },
    { cls: 'cm',  label: 'CM',  title: 'Comentário (//, #, /* */)' },
    { cls: 'fn',  label: 'FN',  title: 'Função / Tipo (nomes de funções, classes)' },
    { cls: 'nu',  label: 'NU',  title: 'Número (42, 3.14, 0xFF)' },
    { cls: 'sp',  label: 'SP',  title: 'Especial ($var, this, @decorator)' },
];

export class CodeExamplePlugin implements VtbPlugin {
    readonly selectorHints = ['pre.code-example', '.code-example', '[data-vtb-id="code-example"]'];
    readonly panelId = 'vtb-code-example';

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e =>
            (e.tagName === 'PRE' && e.classList.contains('code-example')) || 
            e.dataset.vtbId === 'code-example'
        );
        if (!el) return null;
        return { type: this.panelId, label: 'Bloco de Código', el };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const pre = ctx.el;

        // Detectar linguagem da classe
        const langClass = Array.from(pre.classList)
            .find(c => c.startsWith('lang-'));
        const currentLang = langClass ? langClass.replace('lang-', '') : '';

        const langSelect = registry.q<HTMLSelectElement>('vtb-code-lang');
        if (langSelect) langSelect.value = currentLang;

        // Mostrar contagem de tokens
        this._updateTokenCount(pre, registry);
    }

    setup(registry: IVtbRegistry): void {
        // Popula select de linguagens
        const langSelect = registry.q<HTMLSelectElement>('vtb-code-lang');
        if (langSelect && langSelect.options.length <= 1) {
            langSelect.innerHTML = '';
            for (const lang of LANGUAGES) {
                const opt = document.createElement('option');
                opt.value = lang.value;
                opt.textContent = lang.label;
                langSelect.appendChild(opt);
            }
        }

        // Eventos
        langSelect?.addEventListener('change', () => this._applyLanguage(registry));

        registry.q('vtb-code-edit-btn')?.addEventListener('click', () => this._openRawEditor(registry));
        registry.q('vtb-code-edit-save')?.addEventListener('click', () => this._saveRawEditor(registry));
        registry.q('vtb-code-edit-cancel')?.addEventListener('click', () => this._closeRawEditor(registry));
        registry.q('vtb-code-clear-hl')?.addEventListener('click', () => this._clearHighlights(registry));
        registry.q('vtb-code-duplicate')?.addEventListener('click', () => this._duplicate(registry));
        registry.q('vtb-code-remove')?.addEventListener('click', () => this._remove(registry));
        registry.q('vtb-code-auto')?.addEventListener('click', () => this._autoHighlight(registry));

        // Botões de token
        for (const token of TOKEN_MAP) {
            registry.q('vtb-code-tok-' + token.cls)?.addEventListener('click', () => {
                this._wrapSelectionWithToken(registry, token.cls);
            });
        }

        // Atalho Ctrl+Enter na textarea de edição raw
        registry.q<HTMLTextAreaElement>('vtb-code-raw')?.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' && e.ctrlKey) {
                e.preventDefault();
                this._saveRawEditor(registry);
            }
        });
    }

    // ── Linguagem ──────────────────────────────────────────────────────────────

    private _applyLanguage(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const pre = ctx.el;
        const lang = registry.q<HTMLSelectElement>('vtb-code-lang')?.value || '';

        // Remove classes lang-* anteriores
        const toRemove = Array.from(pre.classList).filter(c => c.startsWith('lang-'));
        toRemove.forEach(c => pre.classList.remove(c));

        if (lang) pre.classList.add('lang-' + lang);
        registry.sync();
    }

    // ── Editor Raw (textarea) ──────────────────────────────────────────────────

    private _openRawEditor(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        const raw = registry.q<HTMLTextAreaElement>('vtb-code-raw');
        const form = registry.q<HTMLElement>('vtb-code-raw-form');
        if (!raw || !form) return;

        // Extrai texto puro
        raw.value = ctx.el.textContent || '';
        form.style.display = 'block';
        raw.focus();
    }

    private _saveRawEditor(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        const raw = registry.q<HTMLTextAreaElement>('vtb-code-raw');
        const form = registry.q<HTMLElement>('vtb-code-raw-form');
        if (!raw || !form) return;

        // Substitui conteúdo do pre (escapa HTML)
        ctx.el.textContent = raw.value;
        form.style.display = 'none';
        registry.sync();
    }

    private _closeRawEditor(registry: IVtbRegistry): void {
        const form = registry.q<HTMLElement>('vtb-code-raw-form');
        if (form) form.style.display = 'none';
    }

    // ── Tokens de highlight ────────────────────────────────────────────────────

    /**
     * Envolve a seleção no visualEditor com <span class="TOKEN">.
     * Se já está dentro de um span com essa classe, remove (toggle).
     */
    private _wrapSelectionWithToken(registry: IVtbRegistry, tokenCls: string): void {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;

        const range = sel.getRangeAt(0);
        const ctx = registry.activeCtx;
        if (!ctx) return;

        // Verifica se a seleção está dentro do pre
        if (!ctx.el.contains(range.commonAncestorContainer)) return;

        // Verifica se já está dentro de um span com essa classe (unwrap)
        const parentSpan = range.commonAncestorContainer.parentElement;
        if (parentSpan && parentSpan.tagName === 'SPAN' && parentSpan.classList.contains(tokenCls)) {
            // Unwrap: substitui o span pelo seu textContent
            const text = document.createTextNode(parentSpan.textContent || '');
            parentSpan.parentNode?.replaceChild(text, parentSpan);
            registry.sync();
            this._updateTokenCount(ctx.el, registry);
            return;
        }

        // Wrap
        const span = document.createElement('span');
        span.className = tokenCls;
        try {
            range.surroundContents(span);
        } catch {
            // surroundContents falha se o range cruza boundaries de nós
            // Fallback: extracta o conteúdo, cria o span, e insere
            const fragment = range.extractContents();
            span.appendChild(fragment);
            range.insertNode(span);
        }
        registry.sync();
        this._updateTokenCount(ctx.el, registry);
    }

    private _clearHighlights(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        // Substitui todos os spans de token pelo seu textContent
        const tokens = ctx.el.querySelectorAll<HTMLSpanElement>('.kw, .str, .cm, .fn, .nu, .sp');
        tokens.forEach(span => {
            const text = document.createTextNode(span.textContent || '');
            span.parentNode?.replaceChild(text, span);
        });

        // Normaliza nós de texto adjacentes
        ctx.el.normalize();
        registry.sync();
        this._updateTokenCount(ctx.el, registry);
    }

    private _updateTokenCount(pre: HTMLElement, registry: IVtbRegistry): void {
        const count = pre.querySelectorAll('.kw, .str, .cm, .fn, .nu, .sp').length;
        const badge = registry.q<HTMLElement>('vtb-code-tok-count');
        if (badge) badge.textContent = String(count) + ' tokens';
    }

    // ── Auto-Highlight (Lexer) ─────────────────────────────────────────────

    private _autoHighlight(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        const pre = ctx.el;
        // Detect language from class or select
        const langClass = Array.from(pre.classList).find(c => c.startsWith('lang-'));
        const lang = langClass ? langClass.replace('lang-', '') : (registry.q<HTMLSelectElement>('vtb-code-lang')?.value || '');

        const code = pre.textContent || '';
        const html = tokenize(code, lang);

        pre.innerHTML = html;
        registry.sync();
        this._updateTokenCount(pre, registry);
    }

    // ── Remover ────────────────────────────────────────────────────────────────

    private _remove(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        ctx.el.remove();
        registry.sync();
        registry.clearSelection();
    }

    // ── Duplicar ───────────────────────────────────────────────────────────────

    private _duplicate(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        const clone = ctx.el.cloneNode(true) as HTMLElement;
        ctx.el.parentElement?.insertBefore(clone, ctx.el.nextSibling);
        registry.sync();
        registry.selectEl(clone);
    }
}
